import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Loader2, CreditCard, CheckCircle2, Plug, ShieldCheck, Link2, Search,
  TrendingUp, Clock, XCircle, RotateCcw, Send, Star, Wallet, RefreshCw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api } from "@/lib/api";
import { toast } from "sonner";

const STATUS_STYLES = {
  paid: "bg-emerald-100 text-emerald-700",
  pending: "bg-amber-100 text-amber-700",
  failed: "bg-red-100 text-red-700",
  refunded: "bg-slate-200 text-slate-600",
  expired: "bg-orange-100 text-orange-700",
  cancelled: "bg-slate-200 text-slate-600",
};

const money = (n, cur = "GBP") =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency: cur || "GBP" }).format(n || 0);

// Coerce any backend value into a safe React child. Some payment providers return
// account_name/account_email as nested objects; rendering those directly throws
// React error #31 ("Objects are not valid as a React child") and blanks the page.
const text = (v) => {
  if (v == null) return "";
  if (typeof v === "string" || typeof v === "number") return String(v);
  if (typeof v === "object") return v.name || v.business_name || v.display_name || v.address || v.email || v.id || "";
  return String(v);
};

export default function Payments() {
  const [data, setData] = useState(null);
  const [summary, setSummary] = useState(null);
  const [txns, setTxns] = useState([]);
  const [busy, setBusy] = useState("");
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [showLink, setShowLink] = useState(false);
  const [params, setParams] = useSearchParams();

  const loadProviders = useCallback(() => api.get("/payments/providers")
    .then((r) => setData(r.data))
    .catch(() => setData((d) => d || { providers: [], settings: { delivery: "smart", currency: "GBP", policy: "none" }, _error: true })), []);
  const loadSummary = useCallback(() => api.get("/payments/summary").then((r) => setSummary(r.data)).catch(() => setSummary(null)), []);
  const loadTxns = useCallback(() => {
    const p = new URLSearchParams();
    if (filter !== "all") p.set("status", filter);
    if (q) p.set("q", q);
    return api.get(`/payments/transactions?${p.toString()}`).then((r) => setTxns(r.data.transactions || [])).catch(() => setTxns([]));
  }, [filter, q]);

  useEffect(() => { loadProviders(); loadSummary(); }, [loadProviders, loadSummary]);
  useEffect(() => { loadTxns(); }, [loadTxns]);

  // Real-time refresh: refetch when a payment webhook fires (broadcast by the notification poller)
  // and on a light interval so the dashboard reflects payments without a page refresh.
  useEffect(() => {
    const refresh = () => { loadSummary(); loadTxns(); };
    window.addEventListener("payment-updated", refresh);
    const iv = setInterval(refresh, 20000);
    return () => { window.removeEventListener("payment-updated", refresh); clearInterval(iv); };
  }, [loadSummary, loadTxns]);

  useEffect(() => {
    if (params.get("connected")) {
      toast.success(`${params.get("connected")} connected successfully`);
      setParams({}, { replace: true });
      loadProviders(); loadSummary();
    } else if (params.get("error")) {
      toast.error("Could not connect the payment provider. Please try again.");
      setParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const connect = async (p) => {
    setBusy(p.provider);
    try {
      const r = await api.get(`/payments/connect/${p.provider}`);
      window.location.href = r.data.authorize_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start connection");
      setBusy("");
    }
  };
  const disconnect = async (p) => {
    setBusy(p.provider);
    try { await api.post("/payments/disconnect", { provider: p.provider }); toast.success("Disconnected"); loadProviders(); loadSummary(); }
    catch { toast.error("Could not disconnect"); } finally { setBusy(""); }
  };
  const testConn = async (p) => {
    setBusy(p.provider);
    try { const r = await api.post("/payments/test-connection", { provider: p.provider }); toast.success(`Connection OK — ${r.data.account_name || "account verified"}`); loadProviders(); }
    catch (e) { toast.error(e.response?.data?.detail || "Connection failed"); } finally { setBusy(""); }
  };
  const setActive = async (p) => {
    try { await api.post("/payments/set-active", { provider: p.provider }); toast.success(`${p.name} is now your default`); loadProviders(); loadSummary(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not update"); }
  };
  const saveSettings = async (patch) => {
    const s = { delivery: "smart", currency: "GBP", policy: "none", deposit_type: "percent", deposit_value: 50, ...(data?.settings || {}), ...patch };
    setData((d) => ({ ...d, settings: s }));
    try { await api.put("/payments/settings", s); toast.success("Payment settings saved"); }
    catch { toast.error("Could not save"); }
  };
  const changeDelivery = (v) => saveSettings({ delivery: v });

  if (!data) return <div className="flex items-center gap-2 text-slate-400 py-20 justify-center"><Loader2 className="h-5 w-5 animate-spin" /> Loading payments…</div>;

  const cur = summary?.currency || "GBP";
  const anyConnected = data.providers.some((p) => p.connected);

  const stat = (label, value, Icon, tone) => (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">{label}</span>
        <Icon className={`h-4 w-4 ${tone}`} />
      </div>
      <p className="mt-2 text-2xl font-bold text-slate-900">{value}</p>
    </div>
  );

  return (
    <div data-testid="payments-view">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Payments</h1>
          <p className="text-slate-500 mt-1">Connect your payment account so your AI receptionist can send secure payment links and confirm bookings automatically.</p>
        </div>
        <Button data-testid="new-payment-link-btn" disabled={!anyConnected} onClick={() => setShowLink(true)} className="gap-2 bg-blue-600 hover:bg-blue-700">
          <Link2 className="h-4 w-4" /> New payment link
        </Button>
      </div>

      {/* Stats */}
      <div className="mt-6 grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="payments-stats">
        {stat("Total revenue", money(summary?.total_revenue, cur), TrendingUp, "text-emerald-500")}
        {stat("Today", `${money(summary?.revenue_today, cur)} · ${summary?.payments_today || 0}`, Wallet, "text-blue-500")}
        {stat("Outstanding", `${money(summary?.pending_amount, cur)} · ${summary?.pending_count || 0}`, Clock, "text-amber-500")}
        {stat("Failed", summary?.failed_count || 0, XCircle, "text-red-500")}
        {stat("Refunded", `${money(summary?.refunded_amount, cur)} · ${summary?.refunded_count || 0}`, RotateCcw, "text-slate-500")}
      </div>

      {/* Providers */}
      <h2 className="mt-8 text-xs font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5"><CreditCard className="h-3.5 w-3.5" /> Payment providers</h2>
      <div className="mt-3 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {data.providers.map((p) => (
          <div key={p.provider} data-testid={`provider-${p.provider}`} className={`rounded-xl border bg-white p-4 flex flex-col ${p.active ? "border-blue-300 ring-1 ring-blue-100" : "border-slate-200"}`}>
            <div className="flex items-start justify-between">
              <div className="h-9 w-9 rounded-lg bg-blue-50 flex items-center justify-center text-blue-600"><CreditCard className="h-4.5 w-4.5" /></div>
              {!p.available
                ? <span className="rounded-full bg-slate-100 text-slate-500 text-[11px] font-semibold px-2 py-0.5">Setup pending</span>
                : <span data-testid={`provider-status-${p.provider}`} className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${p.connected ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{p.connected ? <><CheckCircle2 className="h-3 w-3" /> Connected</> : "Not connected"}</span>}
            </div>
            <p className="mt-3 font-semibold text-slate-900 text-sm flex items-center gap-1.5">{text(p.name)}{p.active && <Star className="h-3.5 w-3.5 text-blue-500 fill-blue-500" title="Default" />}</p>
            <p className="text-[11px] text-slate-400 mt-0.5 leading-snug">{text(p.blurb)}</p>
            {p.connected && (
              <div className="mt-2 text-[11px] text-slate-500 space-y-0.5">
                {text(p.account_name) && <p className="truncate">Account: <b className="text-slate-700">{text(p.account_name)}</b></p>}
                {text(p.account_email) && <p className="truncate">{text(p.account_email)}</p>}
                {p.last_sync && <p>Last sync: {new Date(p.last_sync).toLocaleDateString()}</p>}
              </div>
            )}
            {!p.connected && p.available && <p className="mt-1 flex items-center gap-1 text-[11px] text-slate-400"><ShieldCheck className="h-3 w-3" /> Secure OAuth</p>}
            <div className="mt-auto pt-3 space-y-1.5">
              {!p.available ? (
                <Button disabled variant="outline" className="w-full h-8 text-xs text-slate-400">Coming soon</Button>
              ) : p.connected ? (
                <>
                  {!p.active && <Button data-testid={`set-active-${p.provider}`} onClick={() => setActive(p)} className="w-full h-8 text-xs bg-slate-900 hover:bg-slate-800">Make default</Button>}
                  <div className="flex gap-1.5">
                    <Button data-testid={`test-${p.provider}`} variant="outline" onClick={() => testConn(p)} disabled={busy === p.provider} className="flex-1 h-8 text-xs">{busy === p.provider ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><RefreshCw className="h-3 w-3 mr-1" /> Test</>}</Button>
                    <Button data-testid={`disconnect-${p.provider}`} variant="outline" onClick={() => disconnect(p)} disabled={busy === p.provider} className="flex-1 h-8 text-xs text-red-600 hover:text-red-700">Disconnect</Button>
                  </div>
                </>
              ) : (
                <Button data-testid={`connect-${p.provider}`} onClick={() => connect(p)} disabled={busy === p.provider} className="w-full h-8 text-xs bg-blue-600 hover:bg-blue-700 gap-1">{busy === p.provider ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Plug className="h-3 w-3" /> Connect</>}</Button>
              )}
            </div>
          </div>
        ))}
      </div>

      {data._error && (
        <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800" data-testid="payments-error-banner">
          Some payment data couldn't load just now. The page still works — try refreshing in a moment.
        </div>
      )}

      {/* Payment policy */}
      <div className="mt-4 rounded-xl border border-slate-200 bg-white p-4" data-testid="payment-policy-card">
        <p className="font-semibold text-slate-900 text-sm flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-blue-600" /> When to require payment</p>
        <p className="text-[12px] text-slate-500 mt-0.5">Default for all bookings. You can override this per service in Services &amp; Pricing.</p>
        <div className="mt-3 flex flex-wrap items-end gap-3">
          <div>
            <Label className="text-xs">Requirement</Label>
            <Select value={data.settings?.policy || "none"} onValueChange={(v) => saveSettings({ policy: v })}>
              <SelectTrigger data-testid="policy-select" className="w-56 h-9 mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No payment required</SelectItem>
                <SelectItem value="deposit">Deposit required</SelectItem>
                <SelectItem value="full">Full payment required</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {data.settings?.policy === "deposit" && (
            <>
              <div>
                <Label className="text-xs">Deposit type</Label>
                <Select value={data.settings?.deposit_type || "percent"} onValueChange={(v) => saveSettings({ deposit_type: v })}>
                  <SelectTrigger data-testid="deposit-type-select" className="w-40 h-9 mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percent">Percentage</SelectItem>
                    <SelectItem value="fixed">Fixed amount</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">{data.settings?.deposit_type === "fixed" ? `Amount (${cur})` : "Percentage (%)"}</Label>
                <Input data-testid="deposit-value-input" type="number" defaultValue={data.settings?.deposit_value ?? 50}
                  onBlur={(e) => saveSettings({ deposit_value: Number(e.target.value) || 0 })} className="w-32 h-9 mt-1" />
              </div>
            </>
          )}
        </div>
      </div>

      {/* Delivery preference */}
      <div className="mt-6 rounded-xl border border-slate-200 bg-white p-4 flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="font-semibold text-slate-900 text-sm flex items-center gap-1.5"><Send className="h-4 w-4 text-blue-600" /> How to send payment links</p>
          <p className="text-[12px] text-slate-500 mt-0.5">Your AI will send the link to callers using this order.</p>
        </div>
        <Select value={data.settings?.delivery || "smart"} onValueChange={changeDelivery}>
          <SelectTrigger data-testid="delivery-select" className="w-56 h-9"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="smart">Smart (WhatsApp → SMS → Email)</SelectItem>
            <SelectItem value="whatsapp">WhatsApp only</SelectItem>
            <SelectItem value="sms">SMS only</SelectItem>
            <SelectItem value="email">Email only</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Transactions */}
      <div className="mt-8 flex items-center justify-between gap-3 flex-wrap">
        <h2 className="text-xs font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5"><Wallet className="h-3.5 w-3.5" /> Transactions</h2>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="h-3.5 w-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <Input data-testid="txn-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, phone…" className="h-8 w-48 pl-8 text-xs" />
          </div>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger data-testid="txn-filter" className="w-32 h-8 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="paid">Paid</SelectItem>
              <SelectItem value="pending">Outstanding</SelectItem>
              <SelectItem value="failed">Failed</SelectItem>
              <SelectItem value="refunded">Refunded</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="mt-3 rounded-xl border border-slate-200 bg-white overflow-hidden">
        {txns.length === 0 ? (
          <div className="py-14 text-center text-sm text-slate-400" data-testid="txn-empty">No transactions yet. Payments taken by your AI receptionist will appear here.</div>
        ) : (
          <table className="w-full text-sm" data-testid="txn-table">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left font-semibold px-4 py-2.5">Customer</th>
                <th className="text-left font-semibold px-4 py-2.5">Description</th>
                <th className="text-right font-semibold px-4 py-2.5">Amount</th>
                <th className="text-left font-semibold px-4 py-2.5">Provider</th>
                <th className="text-left font-semibold px-4 py-2.5">Status</th>
                <th className="text-left font-semibold px-4 py-2.5">Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {txns.map((t) => (
                <tr key={t.id} data-testid={`txn-row-${t.id}`} className="hover:bg-slate-50">
                  <td className="px-4 py-2.5"><div className="font-medium text-slate-800">{text(t.customer_name) || "—"}</div><div className="text-xs text-slate-400">{text(t.phone) || text(t.email) || ""}</div></td>
                  <td className="px-4 py-2.5 text-slate-600">{text(t.description)}</td>
                  <td className="px-4 py-2.5 text-right font-semibold text-slate-900">{money(t.amount, t.currency)}</td>
                  <td className="px-4 py-2.5 capitalize text-slate-500">{text(t.provider)}</td>
                  <td className="px-4 py-2.5"><span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold capitalize ${STATUS_STYLES[t.status] || "bg-slate-100 text-slate-600"}`}>{t.status === "pending" ? "outstanding" : t.status}</span></td>
                  <td className="px-4 py-2.5 text-xs text-slate-400">{t.created_at ? new Date(t.created_at).toLocaleString() : ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <NewLinkDialog open={showLink} onClose={() => setShowLink(false)} onDone={() => { loadTxns(); loadSummary(); }} cur={cur} />
    </div>
  );
}

function NewLinkDialog({ open, onClose, onDone, cur }) {
  const [form, setForm] = useState({ amount: "", description: "", customer_name: "", phone: "", email: "", deliver: "auto" });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.amount || Number(form.amount) <= 0) return toast.error("Enter a valid amount");
    setBusy(true);
    try {
      const r = await api.post("/payments/create-link", { ...form, amount: Number(form.amount) });
      setResult(r.data);
      toast.success(r.data.delivered_via ? `Link sent via ${r.data.delivered_via}` : "Payment link created");
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create link"); }
    finally { setBusy(false); }
  };

  const close = () => { setResult(null); setForm({ amount: "", description: "", customer_name: "", phone: "", email: "", deliver: "auto" }); onClose(); };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent data-testid="new-link-dialog">
        <DialogHeader><DialogTitle>Create a payment link</DialogTitle></DialogHeader>
        {result ? (
          <div className="space-y-3" data-testid="link-result">
            <p className="text-sm text-slate-600">Secure payment link created{result.delivered_via ? ` and sent via ${result.delivered_via}` : ""}:</p>
            <div className="rounded-lg border bg-slate-50 p-3 text-xs break-all">{result.link_url}</div>
            <Button className="w-full" onClick={() => { navigator.clipboard.writeText(result.link_url); toast.success("Copied"); }}>Copy link</Button>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Amount ({cur})</Label><Input data-testid="link-amount" type="number" value={form.amount} onChange={(e) => set("amount", e.target.value)} placeholder="25.00" className="mt-1" /></div>
              <div><Label className="text-xs">Description</Label><Input data-testid="link-description" value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="Booking deposit" className="mt-1" /></div>
            </div>
            <div><Label className="text-xs">Customer name</Label><Input data-testid="link-name" value={form.customer_name} onChange={(e) => set("customer_name", e.target.value)} className="mt-1" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Phone</Label><Input data-testid="link-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+44…" className="mt-1" /></div>
              <div><Label className="text-xs">Email</Label><Input data-testid="link-email" value={form.email} onChange={(e) => set("email", e.target.value)} className="mt-1" /></div>
            </div>
            <div>
              <Label className="text-xs">Send via</Label>
              <Select value={form.deliver} onValueChange={(v) => set("deliver", v)}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">Use my default</SelectItem>
                  <SelectItem value="sms">SMS</SelectItem>
                  <SelectItem value="whatsapp">WhatsApp</SelectItem>
                  <SelectItem value="email">Email</SelectItem>
                  <SelectItem value="link_only">Just create link</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        )}
        {!result && (
          <DialogFooter>
            <Button variant="outline" onClick={close}>Cancel</Button>
            <Button data-testid="link-submit" onClick={submit} disabled={busy} className="bg-blue-600 hover:bg-blue-700 gap-2">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Create link</Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
